import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";
import SectionLayout from "../components/SectionLayout";
import ChartCard, { useChartData } from "../components/charts/ChartCard";
import { Grafico, ejes, ejeTemporal, tooltipStyle, legendProps, barCursor, ChartLoading, ChartError, CHART_SERIES } from "../components/charts/chartTheme";
import { fetchSerie, SERIES_BC, INICIO_SERIE, variacionAnual } from "../services/bancoCentral";
import { loadCSV, DATA } from "../services/csvLoader";
import { BRAND, SECTION_COLORS } from "../theme";

const INDICADORES = [
  { id: "ipc",      label: "Inflación mensual (IPC)" },
  { id: "imacec",   label: "IMACEC" },
  { id: "pib",      label: "PIB" },
  { id: "fbcf",     label: "Formación Bruta de Capital Fijo" },
  { id: "consumo",  label: "Consumo de Hogares" },
  { id: "tcambio",  label: "Tipo de Cambio" },
  { id: "cobre",    label: "Precio del Cobre" },
  { id: "deuda",    label: "Deuda Bruta del Gobierno Central" },
  { id: "gini",         label: "Gini antes y después de impuestos" },
  { id: "riqueza",      label: "Ingreso vs riqueza" },
  { id: "exportaciones",label: "Exportaciones por sector" },
  { id: "socios",       label: "Destino de las exportaciones" },
];

function getYear(fecha) {
  return String(fecha || "").split("-").find((part) => /^\d{4}$/.test(part)) || "";
}

function economyTimeAxis(data) {
  const seen = new Set();
  const ticks = data.reduce((result, row) => {
    const year = getYear(row.fecha);
    if (year && !seen.has(year)) {
      seen.add(year);
      result.push(row.fecha);
    }
    return result;
  }, []);
  return ticks.length >= 3
    ? { ticks, interval: 0, tickFormatter: getYear }
    : { tickCount: 8, interval: "preserveStartEnd" };
}

/* startDate por defecto sale de INICIO_SERIE (ver bancoCentral.js): así el
   precargado del arranque y el gráfico piden exactamente la misma clave. */

function useBC(serieId, startDate = INICIO_SERIE[serieId], yoy = false, registerData = true) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const registerChartData = useChartData();
  useEffect(() => {
    setLoading(true);
    fetchSerie({ serieId, startDate })
      .then((d) => { setData(yoy ? variacionAnual(d) : d); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [serieId, startDate, yoy]);
  useEffect(() => {
    if (registerData) registerChartData(data);
  }, [data, registerData, registerChartData]);
  return { data, loading, error };
}

function BCLineChart({ serieId, startDate, color = BRAND.nodoBlue, yLabel, referencia, yoy = false }) {
  const { data, loading, error } = useBC(serieId, startDate, yoy);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;
  const display = data.slice(-120);
  return (
    <Grafico width="100%" height={300}>
      <LineChart data={display} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="fecha" {...ejes.x} {...ejeTemporal} {...economyTimeAxis(display)} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}${yLabel || ""}`} />
        <Tooltip {...tooltipStyle} formatter={(v) => [`${v.toFixed(2)}${yLabel || ""}`, "Valor"]} />
        {referencia != null && (
          <ReferenceLine y={referencia} stroke={BRAND.fesRed} strokeDasharray="4 4" label={{ value: `Ref ${referencia}`, fontSize: 11 }} />
        )}
        <Line type="monotone" dataKey="valor" stroke={color} dot={false} strokeWidth={2} />
      </LineChart>
    </Grafico>
  );
}

function BCBarChart({ serieId, startDate, color = BRAND.nodoBlue, yLabel }) {
  const { data, loading, error } = useBC(serieId, startDate);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;
  const display = data.slice(-60);
  return (
    <Grafico width="100%" height={300}>
      <BarChart data={display} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="fecha" {...ejes.x} {...ejeTemporal} {...economyTimeAxis(display)} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}${yLabel || ""}`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => [`${v.toFixed(2)}${yLabel || ""}`, "Valor"]} />
        <Bar dataKey="valor" fill={color} radius={[4, 4, 0, 0]} maxBarSize={36} />
      </BarChart>
    </Grafico>
  );
}

/* Doble serie IPC */

function IPCChart() {
  const principal = useBC(SERIES_BC.IPC_VAR_ANUAL, undefined, false, false);
  const sinVol    = useBC(SERIES_BC.IPC_SIN_VOL, undefined, false, false);
  const registerChartData = useChartData();
  const merged = useMemo(() => principal.data.map((p) => {
    const sv = sinVol.data.find((s) => s.fecha === p.fecha);
    return { fecha: p.fecha, "IPC Anual": p.valor, "Sin volátiles": sv?.valor };
  }), [principal.data, sinVol.data]);
  useEffect(() => {
    registerChartData(principal.loading || sinVol.loading ? [] : merged);
  }, [merged, principal.loading, registerChartData, sinVol.loading]);
  if (principal.loading || sinVol.loading) return <ChartLoading />;
  if (principal.error) return <ChartError msg={principal.error} />;
  return (
    <Grafico width="100%" height={300}>
      <LineChart data={merged} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="fecha" {...ejes.x} {...ejeTemporal} {...economyTimeAxis(merged)} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} />
        <Tooltip {...tooltipStyle} formatter={(v) => v != null ? `${v.toFixed(2)}%` : "—"} />
        <ReferenceLine y={0} stroke="#B7BECF" />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="IPC Anual" stroke={BRAND.fesRed} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="Sin volátiles" stroke={BRAND.nodoBlue} dot={false} strokeWidth={2} strokeDasharray="5 3" />
      </LineChart>
    </Grafico>
  );
}

/* IMACEC doble */

function IMACECChart() {
  const desest = useBC(SERIES_BC.IMACEC_DESEST, undefined, false, false);
  const empalm = useBC(SERIES_BC.IMACEC_EMPALM, undefined, false, false);
  const registerChartData = useChartData();
  const merged = useMemo(() => desest.data.map((d) => {
    const e = empalm.data.find((x) => x.fecha === d.fecha);
    return { fecha: d.fecha, Desestacionalizado: d.valor, "Serie empalmada": e?.valor };
  }), [desest.data, empalm.data]);
  useEffect(() => {
    registerChartData(desest.loading || empalm.loading ? [] : merged);
  }, [desest.loading, empalm.loading, merged, registerChartData]);
  if (desest.loading || empalm.loading) return <ChartLoading />;
  if (desest.error) return <ChartError msg={desest.error} />;
  return (
    <Grafico width="100%" height={300}>
      <LineChart data={merged} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="fecha" {...ejes.x} {...ejeTemporal} {...economyTimeAxis(merged)} />
        <YAxis {...ejes.y} />
        <Tooltip {...tooltipStyle} />
        <ReferenceLine y={100} stroke="#B7BECF" />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Desestacionalizado" stroke={BRAND.nodoBlue} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="Serie empalmada" stroke={BRAND.fesRed} dot={false} strokeWidth={2} strokeDasharray="5 3" />
      </LineChart>
    </Grafico>
  );
}

/* ── Desigualdad (WID) y comercio exterior (Atlas) — desde CSV, no del BC ── */

function useCSV(url) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const registerChartData = useChartData();
  useEffect(() => {
    loadCSV(url)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(String(e)); setLoading(false); });
  }, [url]);
  useEffect(() => { registerChartData(data); }, [data, registerChartData]);
  return { data, loading, error };
}

/** Ingreso vs riqueza en el último año: el contraste entre ambas reparticiones. */
function GraficoComparacion() {
  const { data, loading, error } = useCSV(DATA.economia.widComparacion);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data
    .filter((r) => r.grupo)
    .map((r) => ({ grupo: r.grupo, "Del ingreso": r.ingreso, "De la riqueza": r.riqueza }));

  return (
    <Grafico width="100%" height={320}>
      <BarChart data={mapped} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="grupo" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => `${Number(v).toFixed(1)}%`} />
        <Legend {...legendProps} />
        <Bar dataKey="Del ingreso" fill={BRAND.nodoBlue} radius={[4, 4, 0, 0]} maxBarSize={46} />
        <Bar dataKey="De la riqueza" fill={BRAND.fesRed} radius={[4, 4, 0, 0]} maxBarSize={46} />
      </BarChart>
    </Grafico>
  );
}

/** Gini antes y después de impuestos y transferencias. */
function GraficoGini() {
  const { data, loading, error } = useCSV(DATA.economia.widGini);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({
    anio: r.anio,
    "Antes de impuestos": r.gini_pre,
    "Después de impuestos y transferencias": r.gini_post,
  }));

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={mapped} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="anio" {...ejes.x} {...ejeTemporal} />
        <YAxis {...ejes.y} domain={["dataMin - 0.02", "dataMax + 0.02"]} tickFormatter={(v) => v.toFixed(2)} />
        <Tooltip {...tooltipStyle} formatter={(v) => v?.toFixed(3)} />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Antes de impuestos" stroke={BRAND.fesRed} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="Después de impuestos y transferencias" stroke={BRAND.nodoBlue} dot={false} strokeWidth={2} />
      </LineChart>
    </Grafico>
  );
}

/** Exportaciones por sector, en participación del total. */
function GraficoExportaciones() {
  const { data, loading, error } = useCSV(DATA.economia.aecSectores);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const años = [...new Set(data.map((r) => r.anio))].sort();
  // Los sectores chicos se agrupan: nueve áreas apiladas ya son ilegibles.
  const totales = new Map();
  for (const r of data) totales.set(r.sector, (totales.get(r.sector) || 0) + r.millones_usd);
  const principales = [...totales.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([s]) => s);

  const mapped = años.map((anio) => {
    const filas = data.filter((r) => r.anio === anio);
    const fila = { anio };
    let resto = 0;
    for (const r of filas) {
      if (principales.includes(r.sector)) fila[r.sector] = r.share;
      else resto += r.share;
    }
    fila["Otros sectores"] = Math.round(resto * 10) / 10;
    return fila;
  });

  const series = [...principales, "Otros sectores"];

  return (
    <Grafico width="100%" height={320}>
      <AreaChart data={mapped} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="anio" {...ejes.x} {...ejeTemporal} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
        <Tooltip {...tooltipStyle} formatter={(v) => `${Number(v).toFixed(1)}%`} />
        <Legend {...legendProps} wrapperStyle={{ ...legendProps.wrapperStyle, fontSize: 11 }} />
        {series.map((s, i) => (
          <Area key={s} type="monotone" dataKey={s} stackId="1"
                stroke={CHART_SERIES[i % CHART_SERIES.length]}
                fill={CHART_SERIES[i % CHART_SERIES.length]} fillOpacity={0.78} strokeWidth={1} />
        ))}
      </AreaChart>
    </Grafico>
  );
}

/** A qué países se va la exportación chilena. */
function GraficoSocios() {
  const { data, loading, error } = useCSV(DATA.economia.aecSocios);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const exportaciones = data.filter((r) => r.flujo === "exportaciones");
  const ultimo = Math.max(...exportaciones.map((r) => r.anio));
  const principales = exportaciones
    .filter((r) => r.anio === ultimo)
    .sort((a, b) => b.share - a.share)
    .slice(0, 6)
    .map((r) => r.pais);

  const años = [...new Set(exportaciones.map((r) => r.anio))].sort();
  const mapped = años.map((anio) => {
    const fila = { anio };
    for (const pais of principales) {
      const r = exportaciones.find((x) => x.anio === anio && x.pais === pais);
      fila[pais] = r ? r.share : null;
    }
    return fila;
  });

  return (
    <Grafico width="100%" height={320}>
      <LineChart data={mapped} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="anio" {...ejes.x} {...ejeTemporal} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} />
        <Tooltip {...tooltipStyle} formatter={(v) => v != null ? `${Number(v).toFixed(1)}%` : "—"} />
        <Legend {...legendProps} />
        {principales.map((pais, i) => (
          <Line key={pais} type="monotone" dataKey={pais}
                stroke={CHART_SERIES[i % CHART_SERIES.length]} dot={false} strokeWidth={2} connectNulls />
        ))}
      </LineChart>
    </Grafico>
  );
}

export default function Economia() {
  return (
    <SectionLayout
      title="Economía"
      description="Indicadores macroeconómicos de Chile: PIB, IMACEC, IPC, tipo de cambio, precio del cobre, deuda pública, formación de capital y consumo de hogares. Series del Banco Central de Chile."
      tag="Macroeconomía"
      palette={SECTION_COLORS.economia}
      indicadores={INDICADORES}
    >
      {/* 1. IPC */}
      <Box id="ipc">
        <ChartCard
          title="Inflación mensual — IPC"
          subtitle="Variación anual del IPC. Serie empalmada base 2023. Fuente: INE vía Banco Central."
          fuente="INE / Banco Central de Chile"
          tag="Mensual"
          csvFilename="ipc_variacion_anual"
          pngFilename="ipc_variacion_anual"
        >
          <IPCChart />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Serie empalmada en 2023 con nueva canasta base. No comparar directamente valores anteriores sin ajuste.
          </Typography>
        </ChartCard>
      </Box>
      {/* 2. IMACEC */}
      <Box id="imacec">
        <ChartCard
          title="IMACEC — Índice Mensual de Actividad Económica"
          subtitle="Variación respecto al mismo período del año anterior. Índice empalmado base 2018."
          fuente="Banco Central de Chile"
          tag="Mensual"
          csvFilename="imacec"
          pngFilename="imacec"
        >
          <IMACECChart />
        </ChartCard>
      </Box>
      {/* 3. PIB */}
      <Box id="pib">
        <ChartCard
          title="PIB — Producto Interno Bruto"
          subtitle="Volumen a precios del año anterior encadenado. Variación YoY trimestral. Base 2018."
          fuente="Banco Central de Chile"
          tag="Trimestral"
          csvFilename="pib_real"
          pngFilename="pib_real"
        >
          <BCLineChart
            serieId={SERIES_BC.PIB_REAL}
            color={BRAND.nodoBlue}
            yLabel="%"
            yoy
          />
        </ChartCard>
      </Box>
      {/* 4. FBCF */}
      <Box id="fbcf">
        <ChartCard
          title="Formación Bruta de Capital Fijo"
          subtitle="Variación respecto al mismo trimestre del año anterior. Volumen encadenado, referencia 2018, serie empalmada."
          fuente="Banco Central de Chile"
          tag="Trimestral"
          csvFilename="fbcf"
          pngFilename="fbcf"
        >
          <BCLineChart
            serieId={SERIES_BC.FBCF}
            color="#6B21A8"
            yLabel="%"
            yoy
          />
        </ChartCard>
      </Box>
      {/* 5. Consumo hogares */}
      <Box id="consumo">
        <ChartCard
          title="Consumo de Hogares e IPSFL"
          subtitle="Consumo individual de bienes y servicios. Variación YoY trimestral. Base 2018."
          fuente="Banco Central de Chile"
          tag="Trimestral"
          csvFilename="consumo_hogares"
          pngFilename="consumo_hogares"
        >
          <BCLineChart
            serieId={SERIES_BC.CONSUMO_HOGARES}
            color="#00A4A6"
            yLabel="%"
            yoy
          />
        </ChartCard>
      </Box>
      {/* 6. Tipo de cambio */}
      <Box id="tcambio">
        <ChartCard
          title="Tipo de Cambio Nominal (USD/CLP)"
          subtitle="Cuántos pesos chilenos cuesta comprar 1 USD. Promedio mensual observado."
          fuente="Banco Central de Chile"
          tag="Mensual"
          csvFilename="tipo_cambio"
          pngFilename="tipo_cambio"
        >
          <BCLineChart
            serieId={SERIES_BC.TIPO_CAMBIO}
            color="#F39C12"
            yLabel=" $"
          />
        </ChartCard>
      </Box>
      {/* 7. Precio cobre */}
      <Box id="cobre">
        <ChartCard
          title="Precio del Cobre (USD/libra)"
          subtitle="Precio mensual promedio del cobre en el mercado internacional."
          fuente="Banco Central de Chile"
          tag="Mensual"
          csvFilename="precio_cobre"
          pngFilename="precio_cobre"
        >
          <BCLineChart
            serieId={SERIES_BC.PRECIO_COBRE}
            color="#E67E22"
          />
        </ChartCard>
      </Box>
      {/* 8. Deuda pública */}
      <Box id="deuda">
        <ChartCard
          title="Deuda Bruta del Gobierno Central (% PIB)"
          subtitle="Deuda bruta del Gobierno Central como porcentaje del PIB. Trimestral."
          fuente="Banco Central de Chile"
          tag="Trimestral"
          csvFilename="deuda_gobierno"
          pngFilename="deuda_gobierno"
        >
          <BCBarChart
            serieId={SERIES_BC.DEUDA_BRUTA}
            color="#6C5CE7"
            yLabel="%"
          />
        </ChartCard>
      </Box>

      {/* ===== DESIGUALDAD ===== */}
      <Typography variant="overline" color="text.secondary" fontWeight={700} mt={3} mb={2} display="block">
        Distribución del ingreso y la riqueza
      </Typography>

      {/* 10. Gini pre y post */}
      <Box id="gini">
        <ChartCard
          title="Desigualdad antes y después de la acción del Estado"
          subtitle="Índice de Gini del ingreso, antes y después de impuestos y transferencias, 2000-2022. 0 = igualdad total, 1 = concentración total."
          fuente="World Inequality Database (WID.world)"
          tag="Anual"
          csvFilename="gini_pre_post"
          pngFilename="gini_pre_post"
        >
          <GraficoGini />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            La distancia entre ambas líneas es cuánto corrige el sistema de impuestos y transferencias.
            Mientras más juntas, menos redistribuye.
          </Typography>
        </ChartCard>
      </Box>

      {/* 11. Ingreso vs riqueza */}
      <Box id="riqueza">
        <ChartCard
          title="Ingreso y riqueza: dos repartos distintos"
          subtitle="Participación de cada grupo en el ingreso nacional y en la riqueza personal neta (activos menos deudas), 2022."
          fuente="World Inequality Database (WID.world)"
          tag="Último año"
          csvFilename="ingreso_vs_riqueza"
          pngFilename="ingreso_vs_riqueza"
        >
          <GraficoComparacion />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            El patrimonio está bastante más concentrado que el ingreso: el mismo decil que capta el
            59,5% del ingreso posee el 72% de la riqueza, mientras la mitad más pobre pasa de 8,2% a
            apenas 2%. El 1% está contenido dentro del decil superior, no se suma aparte. Se muestra
            como foto y no como serie porque WID estima la riqueza chilena con un modelo (marca de
            calidad 0) que apenas varía 1,8 puntos en 45 años.
          </Typography>
        </ChartCard>
      </Box>

      {/* ===== COMERCIO EXTERIOR ===== */}
      <Typography variant="overline" color="text.secondary" fontWeight={700} mt={3} mb={2} display="block">
        Comercio exterior
      </Typography>

      {/* 12. Exportaciones por sector */}
      <Box id="exportaciones">
        <ChartCard
          title="Qué exporta Chile"
          subtitle="Participación de cada sección arancelaria en el total exportado, 1995-2024."
          fuente="OEC / BACI-CEPII"
          tag="Anual"
          csvFilename="exportaciones_sector"
          pngFilename="exportaciones_sector"
        >
          <GraficoExportaciones />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Minerales y metales incluyen el cobre en sus dos formas: concentrado y refinado. Sumados
            explican más de la mitad de todo lo que el país exporta. Los sectores menores se agrupan
            en "Otros" para que el gráfico sea legible; el CSV descargable trae las 21 secciones.
          </Typography>
        </ChartCard>
      </Box>

      {/* 13. Socios comerciales */}
      <Box id="socios">
        <ChartCard
          title="A dónde va la exportación chilena"
          subtitle="Participación de los principales destinos en el total exportado, 1995-2024."
          fuente="OEC / BACI-CEPII"
          tag="Anual"
          csvFilename="destinos_exportacion"
          pngFilename="destinos_exportacion"
        >
          <GraficoSocios />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            El giro hacia China es el cambio más marcado del período.
          </Typography>
        </ChartCard>
      </Box>
    </SectionLayout>
  );
}
